import 'swiper/css/bundle';

import Swiper from 'swiper';
import { Manipulation, Navigation, Pagination, Thumbs } from 'swiper/modules';

let showingThumbs = false;
const isDesktop = window.matchMedia('(min-width: 768px)');
let thumbsCarousel: Swiper | null;
let mainCarousel: Swiper | null;

// Load the thumbs slider if screen is > 640px. And always init the
const initThumbs = () => {
  showingThumbs = true;
  thumbsCarousel = new Swiper('#thumbsCarousel', {
    modules: [Navigation],
    spaceBetween: 6,
    slidesPerView: 5,
    slidesPerGroup: 1,
    navigation: {
      nextEl: '.product-thumbs-next',
      prevEl: '.product-thumbs-prev'
    },
    on: {
      init: (el) => {
        fadeInSlider(el);
      }
    }
  });
};

const initMainThumbs = () => {
  if (mainCarousel?.thumbs && thumbsCarousel) {
    mainCarousel.thumbs.swiper = thumbsCarousel;
  }
};

const closeOverlay = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    toggleFullscreen();
  }
  if (e.key === 'ArrowRight') {
    mainCarousel?.slideNext();
  }
  if (e.key === 'ArrowLeft') {
    mainCarousel?.slidePrev();
  }
};

let timer: number = 0;

const toggleFullscreen = () => {
  clearTimeout(timer);

  if (isDesktop.matches) {
    const btnCloseOverlay = document.querySelector<HTMLElement>(
      '.closeProductOverlay'
    );

    if (mainCarousel && !mainCarousel?.el.classList.contains('fullscreen')) {
      const imgToClone = mainCarousel.el.querySelector<HTMLImageElement>(
        '.swiper-slide-visible .product-image'
      );
      const clone = imgToClone?.cloneNode(true);
      if (clone && clone instanceof HTMLElement) {
        clone.setAttribute('placeholder', 'true');
        mainCarousel.el.parentElement?.prepend(clone);
      }
    } else {
      document.querySelector<HTMLElement>('figure[placeholder]')?.remove();
    }

    if (mainCarousel) {
      if (!mainCarousel.el.classList.contains('fullscreen')) {
        mainCarousel.el.classList.add('!z-main-carousel');
        mainCarousel.el.classList.add('fullscreen');
      } else {
        mainCarousel.el.classList.remove('fullscreen');

        // TODO: Find a way to avoid this hack :-)
        timer = setTimeout(() => {
          mainCarousel?.el.classList.remove('!z-main-carousel');
        }, 500);
      }
    }

    if (mainCarousel?.el.classList.contains('fullscreen')) {
      btnCloseOverlay?.classList.remove('hidden');
      document.addEventListener('keydown', closeOverlay);
    } else {
      document.removeEventListener('keydown', closeOverlay);
      btnCloseOverlay?.classList.add('hidden');
    }
  }
};

// Load the main slider
const initMain = () => {
  mainCarousel = new Swiper('#mainCarousel', {
    modules: [Navigation, Thumbs, Pagination, Manipulation],
    spaceBetween: 16,
    freeMode: true,

    watchSlidesProgress: true,
    slidesPerView: 1,
    pagination: {
      el: '.products-pagination'
    },

    navigation: {
      nextEl: '.product-carousel-next',
      prevEl: '.product-carousel-prev'
    },
    thumbs: {
      swiper: thumbsCarousel
    },
    on: {
      click: () => {
        toggleFullscreen();
      },
      init: (el) => {
        fadeInSlider(el);
      }
    }
  });
};

const fadeInSlider = (swiper: Swiper) => {
  swiper.el.classList.remove('opacity-0');
  swiper.el.classList.add('opacity-100');
};

let showingVariant = false;

const notifyMissingProductImages = () => {
  const missingImagesMarker = document.querySelector<HTMLElement>(
    '[data-missing-product-images]'
  );
  if (!missingImagesMarker) {
    return;
  }
  const productTitle =
    missingImagesMarker.getAttribute('data-product-title') || 'Unknown product';
  if (window._bugsnag?.isStarted?.()) {
    window._bugsnag.notify?.(`Missing products images: "${productTitle}".`);
  }
};

const initListeners = () => {
  window.addEventListener('changed.hobbii.product.image', (e) => {
    //When the image is already loaded there is no need to create a new one
    let alreadyExisting = false;
    const carouselImages = document.querySelectorAll<HTMLImageElement>(
      '#mainCarousel figure>img'
    );

    const imageUrl = e instanceof CustomEvent ? e.detail.imageUrl : null;
    carouselImages.forEach((carouselImage, i) => {
      const url = new URL(carouselImage.src);
      const imagePath = url?.pathname;

      if (imagePath && imageUrl?.includes(imagePath)) {
        alreadyExisting = true;
        mainCarousel?.slideTo(i);
      }
    });

    if (alreadyExisting) return;

    const img = new Image();

    img.onload = () => {
      if (mainCarousel) {
        const slideToIndex = mainCarousel.slides.findIndex((a) =>
          a.classList.contains('is-a-variant')
        );
        mainCarousel.slideTo(slideToIndex);
      }
    };

    img.src = imageUrl || window.Hobbii?.fallbackImageUrl;

    if (showingVariant) {
      const slideFigure = document.querySelector<HTMLElement>(
        '.swiper-slide.is-a-variant figure'
      );
      if (slideFigure) {
        slideFigure.innerHTML = '';
        slideFigure.appendChild(img);
      }
    } else {
      const newSlide = document.createElement('div');
      newSlide.className = 'swiper-slide is-a-variant';

      const newFigure = document.createElement('figure');
      newFigure.className = 'product-image md:hover:cursor-zoom-in';
      img.className = 'w-full';

      newFigure.appendChild(img);
      newSlide.appendChild(newFigure);

      mainCarousel?.appendSlide(newSlide);
      showingVariant = true;
    }
  });

  if (isDesktop.matches) {
    const btnCloseOverlay = document.querySelector<HTMLElement>(
      '.closeProductOverlay'
    );
    btnCloseOverlay?.addEventListener('click', toggleFullscreen);
  }
};

const initCarousels = () => {
  notifyMissingProductImages();
  if (isDesktop.matches) {
    initThumbs();
  }
  initMain();
  initMainThumbs();
  initListeners();
};

const init = () => {
  initCarousels();
};

init();

// On resize, destroy thumbs slider on mobiles
window.addEventListener('resize', () => {
  if (!isDesktop.matches && showingThumbs) {
    thumbsCarousel?.destroy();
    showingThumbs = false;
  } else if (isDesktop.matches && !showingThumbs) {
    initThumbs();
    // Destroy main and recreate to make thumbs clickable again
    mainCarousel?.destroy();
    initMain();
    initMainThumbs();
  }
  return;
});
